import React, { Children, isValidElement, cloneElement } from 'react';
import PropTypes from 'prop-types';

/**
 * Clones children components and shallowly merge the given props.
 * See https://reactjs.org/docs/react-api.html#cloneelement for more info about cloneElement
 *
 * Example Usage:
 * const childrenWithProps = cloneChildrenWithProps(props.children, {
 *     property: newValue,
 *     property2: newValue2,
 *     ...
 * });
 */
export function cloneChildrenWithProps(children, props) {
	return Children.map(children, child => {
		// Checking isValidElement is the safe way and avoids a TS error too.
		if (isValidElement(child)) {
			return cloneElement(child, props);
		}

		return child;
	});
}

/**
 * custom hook for combined list of refs
 */
export function useCombinedRefs(...refs) {
	const targetRef = React.useRef();

	React.useEffect(() => {
		refs.forEach(ref => {
			if (!ref) {
				return;
			}

			if (typeof ref === 'function') {
				ref(targetRef.current);
			} else {
				ref.current = targetRef.current;
			}
		});
	}, [refs]);

	return targetRef;
}

export function handleRefs(...refs) {
	return node => {
		refs.forEach(ref => {
			if (typeof ref === 'function') {
				ref(node);
			} else if (ref) {
				ref.current = node;
			}
		});
	};
}

export function applyEventListener(eventListener, args) {
	if (eventListener) {
		eventListener.apply(this, args);
	}
}

export const inlineHelperTestId = process.env.NODE_ENV === 'test' ? 'inline-input-wrapper' : undefined;

export function InlineHelper({ inline, className, children }) {
	return inline || className ? (
		<div className={[className, inline && 'col'].filter(Boolean).join(' ')} data-testid={inline ? inlineHelperTestId : undefined}>
			{children}
		</div>
	) : (
		children
	);
}
InlineHelper.propTypes = {
	inline: PropTypes.bool,
	className: PropTypes.string,
	children: PropTypes.any
};

/**
 * generic HOC to conditionally render a component based off some function results
 */
/**
 *export function withConditionalRender(shouldRenderFn, Component) {
 *
 *	return function ConditionalComponent(props) {
 *		return shouldRenderFn(props) ? <Component {...props}>{ props.children }</Component> : null;
 *	};
 *}
 */
